import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Sun, Moon, Eye, EyeOff, Mail, Lock, User, Building2, Sparkles, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ light }) => (
  <div className="woxora-logo text-3xl">
    <span className="wox" style={light ? { color: '#F2EFE9' } : undefined}>Wox</span>
    <span className="ora">ora</span>
  </div>
);

const features = [
  'AI-powered daily work reports',
  'Smart complaint resolution',
  'Sales pipeline tracking',
  'Employee attendance management',
  'Task & project management',
  'Team collaboration tools',
];

export default function SignupPage() {
  const navigate = useNavigate();
  const { login: googleLogin, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', score: 0 });

  const checkPasswordStrength = async (password) => {
    if (password.length < 3) {
      setPasswordStrength({ strength: '', score: 0 });
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/password-strength`, { params: { password } });
      const strength = response.data.strength;
      const scores = { weak: 33, fair: 66, strong: 100 };
      setPasswordStrength({ strength, score: scores[strength] || 0 });
    } catch (error) {
      console.error('Error checking password:', error);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength.strength) {
      case 'weak': return 'bg-red-500';
      case 'fair': return 'bg-amber-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-slate-200';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.fullName || !form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordStrength.strength === 'weak') {
      toast.error('Please choose a stronger password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email: form.email,
        password: form.password,
        full_name: form.fullName
      }, { withCredentials: true });
      
      setUser(response.data);
      toast.success('Account created! Let\'s set up your company.');
      // Business owner signup → redirect to onboarding
      navigate('/onboarding', { replace: true, state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--woxora-bg)' }}>
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="absolute -right-40 -bottom-40 w-96 h-96 rounded-full blur-3xl" style={{ background: 'rgba(224, 109, 83, 0.12)' }} />
        
        <div className="relative z-10">
          <Logo light />
          <p className="text-sm mt-2" style={{ color: '#555', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            Where business gets done.
          </p>
        </div>
        
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ backgroundColor: 'rgba(224, 109, 83, 0.15)', border: '1px solid rgba(224, 109, 83, 0.3)' }}>
            <Sparkles className="w-4 h-4 text-[#E06D53]" />
            <span className="text-sm text-[#E06D53] font-medium">14-day free trial</span>
          </div>
          
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#F2EFE9', letterSpacing: '-0.02em', lineHeight: '1.15' }}>
            Start your free trial
          </h1>
          <p className="text-lg mb-8" style={{ color: '#777', fontFamily: 'Outfit, sans-serif' }}>
            Create your company workspace and experience the power of AI-driven business intelligence.
          </p>
          
          <div className="space-y-3">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#E06D53]/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#E06D53]" />
                </div>
                <span className="text-[#999]" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="relative z-10">
          <div className="rounded-2xl p-6" style={{ backgroundColor: '#141415', border: '1px solid #242426' }}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>500+</p>
                <p className="text-sm" style={{ color: '#666' }}>Companies</p>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>10K+</p>
                <p className="text-sm" style={{ color: '#666' }}>Users</p>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#F2EFE9', fontFamily: 'JetBrains Mono, monospace' }}>99.9%</p>
                <p className="text-sm" style={{ color: '#666' }}>Uptime</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden"><Logo /></div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="ml-auto">
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building2 className="w-6 h-6 text-[#E06D53]" />
              </div>
              <CardTitle className="text-2xl" data-testid="signup-title">Start your free trial</CardTitle>
              <CardDescription>Create your company account — for business owners</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google OAuth */}
              <Button
                variant="outline"
                className="w-full mb-6 h-12"
                onClick={googleLogin}
                data-testid="google-signup-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center"><Separator /></div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="signup-name"
                      value={form.fullName}
                      onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                      placeholder="John Smith"
                      className="pl-10"
                      data-testid="signup-name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="signup-email">Work Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="signup-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@company.com"
                      className="pl-10"
                      data-testid="signup-email"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => {
                        setForm({ ...form, password: e.target.value });
                        checkPasswordStrength(e.target.value);
                      }}
                      placeholder="Create a strong password"
                      className="pl-10 pr-10"
                      data-testid="signup-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {form.password && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Password strength</span>
                        <span className={`font-medium capitalize ${
                          passwordStrength.strength === 'weak' ? 'text-red-500' :
                          passwordStrength.strength === 'fair' ? 'text-amber-500' :
                          passwordStrength.strength === 'strong' ? 'text-green-500' : ''
                        }`}>{passwordStrength.strength}</span>
                      </div>
                      <Progress value={passwordStrength.score} className={`h-1.5 ${getStrengthColor()}`} />
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="signup-confirm">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="signup-confirm"
                      type="password"
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      placeholder="Confirm your password"
                      className="pl-10"
                      data-testid="signup-confirm"
                    />
                  </div>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#E06D53] hover:bg-[#c95a42] h-11"
                  disabled={loading}
                  data-testid="signup-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-center text-slate-500 mt-6">
                By signing up, you agree to our{' '}
                <a href="#" className="text-[#E06D53] hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-[#E06D53] hover:underline">Privacy Policy</a>
              </p>

              <Separator className="my-6" />

              <div className="text-center">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  Already have an account?
                </p>
                <Link to="/auth">
                  <Button variant="outline" className="w-full" data-testid="go-to-login">
                    Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
          
          {/* Employee notice */}
          <p className="text-xs text-center text-slate-500 mt-4">
            Are you an employee?{' '}
            <Link to="/auth" className="text-[#E06D53] hover:underline">
              Use your invitation link or sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
